import { binName } from './bins';
import { hourLabel, joinList, type Locale } from './i18n';

/**
 * "Denkt mit": short, friendly hints from the family's own data – no AI, no guessing.
 * Every rule looks at things the family already entered (events, what to bring, waste dates)
 * plus the weather forecast, and only speaks up when there is something to do.
 */
export interface HintEvent {
  title: string;
  /** HH:MM, absent = all day */
  start?: string;
  end?: string;
  bring: string[];
  /** Names of the people involved ("Ben"), empty for the whole family. */
  who: string[];
  /** From the timetable (school, kita) – daily routine, never "news". */
  routine?: boolean;
  /** A weekly series – also routine, not worth a "Morgen:" hint. */
  recurring?: boolean;
  waste?: boolean;
}

export interface HintInput {
  /** HH:MM in the household time zone */
  now: string;
  today: HintEvent[];
  tomorrow: HintEvent[];
  weather?: {
    /** First hour today with a good chance of rain (HH:MM). */
    rainFrom?: string;
  };
}

export interface Hint {
  id: string;
  kind: 'bring' | 'rain' | 'waste' | 'tomorrow' | 'voucher' | 'idea';
  text: string;
  /** Where tapping leads (Freizeit hints), if anywhere. */
  href?: string;
}

export const HINT_TEXT = {
  de: {
    bring: (time: string, what: string, items: string) => `${time} ${what} – ${items} einpacken`,
    rainGear: (from: string, what: string, gear: string) =>
      `Regen ab ${from} – ${what}: ${gear} nicht vergessen`,
    rain: (from: string, what: string) => `Regen ab ${from} – ${what}: Schirm einpacken`,
    waste: (bins: string) => `Heute Abend rausstellen: ${bins}`,
    tomorrow: (what: string) => `Morgen: ${what}`,
    tomorrowAt: (time: string, what: string) => `Morgen ${time}: ${what}`,
    lay: (items: string) => ` – ${items} bereitlegen`,
  },
  en: {
    bring: (time: string, what: string, items: string) => `${time} ${what} – pack ${items}`,
    rainGear: (from: string, what: string, gear: string) =>
      `Rain from ${from} – ${what}: don’t forget ${gear}`,
    rain: (from: string, what: string) => `Rain from ${from} – ${what}: take an umbrella`,
    waste: (bins: string) => `Put out tonight: ${bins}`,
    tomorrow: (what: string) => `Tomorrow: ${what}`,
    tomorrowAt: (time: string, what: string) => `Tomorrow ${time}: ${what}`,
    lay: (items: string) => ` – get ${items} ready`,
  },
} as const;

const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const label = (e: HintEvent) => (e.who.length ? `${e.title} (${e.who.join(', ')})` : e.title);

/** Soon = the next 2 hours: early enough to pack, late enough to still be relevant. */
const SOON = 120;
/** "Morgen …" and the waste reminder only from noon on – in the morning today matters. */
const AFTERNOON = 12 * 60;
const RAIN_GEAR = /regen|schirm/i;

export function familyHints(
  { now, today, tomorrow, weather }: HintInput,
  max = 3,
  locale: Locale = 'de',
): Hint[] {
  const tx = HINT_TEXT[locale];
  const list = (items: string[]) => joinList(items, locale);
  const t = toMin(now);
  const hints: Hint[] = [];
  const upcoming = today.filter((e) => e.start && toMin(e.start) >= t && !e.routine);

  // 1 – something to pack for an event that starts soon
  const soon = upcoming.find((e) => e.bring.length && toMin(e.start!) - t <= SOON);
  if (soon) {
    hints.push({
      id: `bring:${soon.title}`,
      kind: 'bring',
      text: tx.bring(soon.start!, label(soon), list(soon.bring)),
    });
  }

  // 2 – rain during an event that is still ahead today
  const rain = weather?.rainFrom;
  if (rain) {
    const wet = upcoming.find((e) => toMin(e.end ?? e.start!) >= toMin(rain));
    if (wet) {
      const gear = wet.bring.find((b) => RAIN_GEAR.test(b));
      hints.push({
        id: `rain:${wet.title}`,
        kind: 'rain',
        text: gear
          ? tx.rainGear(hourLabel(rain, locale), label(wet), gear)
          : tx.rain(hourLabel(rain, locale), label(wet)),
      });
    }
  }

  if (t >= AFTERNOON) {
    // 3 – waste collection tomorrow: the bin goes out tonight
    const bins = tomorrow.filter((e) => e.waste).map((e) => e.title);
    if (bins.length) {
      hints.push({
        id: `waste:${bins.join('+')}`,
        kind: 'waste',
        text: tx.waste(list(bins.map((b) => binName(b, locale)))),
      });
    }

    // 4 – tomorrow's special events (not school, not the weekly football)
    const special = tomorrow.filter((e) => !e.routine && !e.recurring && !e.waste);
    for (const e of special.slice(0, 2)) {
      const bring = e.bring.length ? tx.lay(list(e.bring)) : '';
      hints.push({
        id: `tomorrow:${e.title}`,
        kind: 'tomorrow',
        text: (e.start ? tx.tomorrowAt(e.start, label(e)) : tx.tomorrow(label(e))) + bring,
      });
    }
  }

  return hints.slice(0, max);
}
